
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { createContext, useContext, useState, ReactNode, useEffect, useCallback } from "react";
import { connectWalletService } from "../services/mock";
import { fetchTokenPrices } from "../services/prices";
import { getWalletBalances } from "../services/api";
import { WalletType, Token, TransactionParams } from "../types";
import { TOKEN_CONFIGS } from "../constants";

interface WalletContextProps {
  walletAddress?: string;
  walletType?: WalletType;
  isConnected: boolean;
  isConnecting: boolean;
  isPairing: boolean;
  balances: Token[];
  connect: (type: WalletType) => Promise<void>;
  disconnect: () => void;
  sendTransaction: (params: TransactionParams) => Promise<void>;
  simulateTransaction: (params: Omit<TransactionParams, 'swap'>) => Promise<{ fee: string; summary: string }>;
  generateStealthAddress: (chain: 'SOL' | 'SUI') => Promise<string>;
  refreshBalances: () => Promise<void>;
  preferredChain: 'SOL' | 'SUI';
  setPreferredChain: (chain: 'SOL' | 'SUI') => void;
  defaultTokenMint: string | null;
  setDefaultTokenMint: (mint: string | null) => void;
}

const WalletContext = createContext<WalletContextProps>({} as WalletContextProps);

const initialBalances: Token[] = Object.values(TOKEN_CONFIGS).map(config => ({
    ...config,
    balance: 0,
    priceUsd: 1.0, 
    change24h: 0.0,
}));

export const WalletProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [walletAddress, setWalletAddress] = useState<string>();
  const [walletType, setWalletType] = useState<WalletType>();
  const [isConnecting, setIsConnecting] = useState(false);
  const [isPairing, setIsPairing] = useState(false);
  const [balances, setBalances] = useState(initialBalances);

  const [preferredChain, setPreferredChain] = useState<'SOL' | 'SUI'>(() => {
    return (localStorage.getItem('ghost-preferred-chain') as 'SOL' | 'SUI') || 'SUI';
  });
  const [defaultTokenMint, setDefaultTokenMint] = useState<string | null>(() => {
    return localStorage.getItem('ghost-default-token');
  });

  const refreshBalances = useCallback(async () => {
    try {
      const [apiBalances, prices] = await Promise.all([
          getWalletBalances(walletAddress),
          fetchTokenPrices()
      ]);

      setBalances(prevBalances => 
        prevBalances.map(token => {
          let balance = 0;
          if (apiBalances && typeof apiBalances === 'object') {
              const key = Object.keys(apiBalances).find(k => k.toUpperCase() === token.symbol.toUpperCase());
              if (key) {
                  balance = (apiBalances as any)[key];
              }
          }

          return {
              ...token,
              balance: balance > 0 ? balance : token.balance,
              priceUsd: prices[token.type] ?? token.priceUsd,
          };
        })
      );
    } catch (error) {
      console.error("Failed to fetch wallet data:", error);
    }
  }, [walletAddress]);

  useEffect(() => {
    localStorage.setItem('ghost-preferred-chain', preferredChain);
    const currentDefaultToken = balances.find(b => b.mint === defaultTokenMint);
    if (currentDefaultToken && currentDefaultToken.chain !== preferredChain) {
        setDefaultTokenMint(null);
    }
  }, [preferredChain, balances, defaultTokenMint]);

  useEffect(() => {
    if (defaultTokenMint) {
        localStorage.setItem('ghost-default-token', defaultTokenMint);
    } else {
        localStorage.removeItem('ghost-default-token');
    }
  }, [defaultTokenMint]);

  useEffect(() => {
    refreshBalances();
    const interval = setInterval(refreshBalances, 30000); 
    return () => clearInterval(interval);
  }, [refreshBalances]);

  const connect = async (type: WalletType) => {
    if (type === 'walletconnect') {
      setIsPairing(true);
    }
    setIsConnecting(true);
    try {
      const address = await connectWalletService(type);
      setWalletAddress(address);
      setWalletType(type);
      // Trigger immediate data refresh after connection
      setTimeout(() => refreshBalances(), 500);
    } finally {
      setIsConnecting(false);
      setIsPairing(false);
    }
  };

  const disconnect = () => {
    setWalletAddress(undefined);
    setWalletType(undefined);
    // Reset balances to initial zero state
    setBalances(initialBalances);
  };

  const sendTransaction = async (params: TransactionParams): Promise<void> => {
    console.log("Sending transaction via", walletType, params);
    await new Promise(resolve => setTimeout(resolve, 1500));
    await refreshBalances();
  };

  const simulateTransaction = async (params: Omit<TransactionParams, 'swap'>): Promise<{ fee: string; summary: string }> => {
    await new Promise(resolve => setTimeout(resolve, 400));
    const fee = params.token.chain === 'SOL' ? '0.000005 SOL' : '0.001 SUI';
    let summary = `Send ${params.amount} ${params.token.symbol} to ${params.to.slice(0, 6)}...${params.to.slice(-4)}.`;
    if (params.stealth) {
      summary += " This transaction will use a stealth address for enhanced privacy."
    }
    return { fee, summary };
  };
  
  const generateStealthAddress = async (chain: 'SOL' | 'SUI'): Promise<string> => {
    await new Promise(resolve => setTimeout(resolve, 200));
    const base = walletAddress || "DEFAULT_ADDRESS";
    return `stealth_${chain.toLowerCase()}_${base.slice(0, 4)}...${Math.random().toString(16).slice(2, 8)}`;
  };

  return (
    <WalletContext.Provider value={{ 
        walletAddress, 
        walletType, 
        isConnected: !!walletAddress, 
        isConnecting, 
        isPairing,
        balances, 
        connect, 
        disconnect,
        sendTransaction,
        simulateTransaction,
        generateStealthAddress,
        refreshBalances,
        preferredChain,
        setPreferredChain,
        defaultTokenMint,
        setDefaultTokenMint
    }}>
      {children}
    </WalletContext.Provider>
  );
};

export const useWallet = () => useContext(WalletContext);
